import { isNil } from '../../common'
import { ActivepiecesError, ErrorCode } from '../../common/activepieces-error'
import { Action, ActionType, BranchCondition, BranchExecutionType, emptyCondition, RouterAction, Subflow } from '../actions/action'
import { Connection } from '../connections/connection'
import { FlowVersion } from '../flow-version'
import { Trigger, TriggerType } from '../triggers/trigger'

export type Step = Action | Trigger
type StepWithIndex = Step & {
    dfsIndex: number
}

function isAction(type: ActionType | TriggerType | undefined): type is ActionType {
    return Object.entries(ActionType).some(([, value]) => value === type)
}

function isTrigger(type: ActionType | TriggerType | undefined): type is TriggerType {
    return Object.entries(TriggerType).some(([, value]) => value === type)
}

function getActionOrThrow(name: string, flowVersion: FlowVersion): Action {
    const step = getStepOrThrow(name, flowVersion)
    if (!isAction(step.type)) {
        throw new ActivepiecesError({
            code: ErrorCode.STEP_NOT_FOUND,
            params: {
                stepName: name,
            },
        })
    }
    return step as Action
}

function getTriggerOrThrow(name: string, flowVersion: FlowVersion): Trigger {
    const step = getStepOrThrow(name, flowVersion)
    if (!isTrigger(step.type)) {
        throw new ActivepiecesError({
            code: ErrorCode.STEP_NOT_FOUND,
            params: {
                stepName: name,
            },
        })
    }
    return step as Trigger
}


function getStep(
    stepName: string,
    flowVersion: FlowVersion,
): Step | undefined {
    return getAllSteps(flowVersion).find((step) => step.name === stepName)
}

function getStepOrThrow(name: string, flowVersion: FlowVersion): Step {
    const step = getStep(name, flowVersion)

    if (isNil(step)) {
        throw new ActivepiecesError({
            code: ErrorCode.STEP_NOT_FOUND,
            params: {
                stepName: name,
            },
        })
    }
    return step
}

function transferStep<T extends Step>(
    step: Step,
    transferFunction: (step: T) => T,
): Step {
    const updatedStep = transferFunction(step as T)

    if (updatedStep.type === ActionType.LOOP_ON_ITEMS && updatedStep.subflow) {
        updatedStep.subflow.actions = updatedStep.subflow.actions.map((action) =>
            transferStep(action, transferFunction) as Action,
        )
    }
    else if (updatedStep.type === ActionType.ROUTER && updatedStep.children) {
        updatedStep.children = updatedStep.children.map((child) => {
            if (!child) return null
            return {
                ...child,
                actions: child.actions.map((action) =>
                    transferStep(action, transferFunction) as Action,
                ),
            }
        })
    }

    return updatedStep
}


function transferFlow<T extends Step>(
    flowVersion: FlowVersion,
    transferFunction: (step: T) => T,
): FlowVersion {
    const clonedFlow = JSON.parse(JSON.stringify(flowVersion))
    clonedFlow.triggers = clonedFlow.triggers.map((trigger: Step) =>
        transferStep(trigger, transferFunction),
    )

    clonedFlow.actions = clonedFlow.actions.map((action: Step) =>
        transferStep(action, transferFunction),
    )
    return clonedFlow
}

function getAllSteps(
    flowVersion: FlowVersion,
): Step[] {
    const allSteps: Step[] = []
    const visited = new Set<string>()

    function addSteps(steps: Step[]): void {
        steps.forEach((step) => {
            if (!visited.has(step.name)) {
                visited.add(step.name)
                allSteps.push(step)
                if (step.type === ActionType.LOOP_ON_ITEMS && step.subflow) {
                    addSteps(step.subflow.actions)
                }
                else if (step.type === ActionType.ROUTER) {
                    for (const child of step.children) {
                        if (child) {
                            addSteps(child.actions)
                        }
                    }
                }
            }
        })
    }

    addSteps([...flowVersion.triggers, ...flowVersion.actions])

    return allSteps
}

function getAllStepsInExecutionOrder(flowVersion: FlowVersion): Step[] {
    const orderedSteps: Step[] = []
    const visited = new Set<string>()
    
    const stepMap = new Map<string, Step>(
        getAllSteps(flowVersion).map(step => [step.name, step]),
    )

    const processTargets = (context: FlowVersion | Subflow, currentStepName: string) => {
        const targets = getStepTargets(context, currentStepName)
        for (const targetName of targets) {
            traverse(targetName, context)
        }
    }

    const traverse = (stepName: string, context: FlowVersion | Subflow): void => {
        if (visited.has(stepName)) return

        const step = stepMap.get(stepName)
        
        if (!step) return

        visited.add(stepName)
        orderedSteps.push(step)

        if (step.type === ActionType.LOOP_ON_ITEMS && step.subflow) {
            processTargets(step.subflow, step.name)
        } 
        else if (step.type === ActionType.ROUTER && step.children) {
            step.children.forEach(branch => {
                if (branch) processTargets(branch, step.name)
            })
        }

        processTargets(context, step.name)
    }

    for (const stepName of stepMap.keys()) {
        if (!visited.has(stepName)) {
            traverse(stepName, flowVersion)
        }
    }

    return orderedSteps
}

const createBranch = (branchName: string, conditions: BranchCondition[][] | undefined) => {
    return {
        conditions: conditions ?? [[emptyCondition]],
        branchType: BranchExecutionType.CONDITION,
        branchName,
    }
}

function findSubflowForConnection(
    flow: FlowVersion | Subflow,
    sourceId: string,
    targetId: string,
): Subflow | undefined {
    const actions: Step[] = flow.actions || []
    for (const action of actions) {
        if (action.type === ActionType.LOOP_ON_ITEMS && action.subflow) {
            const subflowStepNames = action.subflow.actions.map((step: Step) => step.name)
            if (subflowStepNames.includes(sourceId) && subflowStepNames.includes(targetId)) {
                return action.subflow
            }
            const nestedSubflow = findSubflowForConnection(action.subflow, sourceId, targetId)
            if (nestedSubflow) {
                return nestedSubflow
            }
        }
        else if (action.type === ActionType.ROUTER && Array.isArray(action.children)) {
            for (const child of action.children) {
                if (child) {
                    const childStepNames = child.actions.map((step: Step) => step.name)
                    if (childStepNames.includes(sourceId) && childStepNames.includes(targetId)) {
                        return child
                    }
                    const nestedSubflow = findSubflowForConnection(child, sourceId, targetId)
                    if (nestedSubflow) {
                        return nestedSubflow
                    }
                }
            }
        }
    }
    return undefined
}

function _findPathToStep(
    stepToFind: Step,
    currentStep: Step,
    flowVersion: FlowVersion,
    allSteps: Step[],
    path: StepWithIndex[] = [],
    indexMap: Map<string, number>,
    visited: Set<string> = new Set(),
    dfsIndex: { count: number },
): StepWithIndex[] | undefined {
    if (visited.has(currentStep.name)) {
        return undefined
    }

    visited.add(currentStep.name)
    indexMap.set(currentStep.name, dfsIndex.count++)

    const newPath = path.concat({
        ...currentStep,
        dfsIndex: indexMap.get(currentStep.name) || 1,
    })

    if (currentStep.name === stepToFind.name) {
        return newPath
    }

    const allTargetNames = getStepTargets(flowVersion, currentStep.name)

    for (const targetName of allTargetNames) {
        const nextStep = allSteps.find((step) => step.name === targetName)
        if (nextStep) {
            const result = _findPathToStep(
                stepToFind,
                nextStep,
                flowVersion,
                allSteps,
                newPath,
                indexMap,
                visited,
                dfsIndex,
            )
            if (result) return result
        }
    }

    visited.delete(currentStep.name)
    return undefined
}

function findPathToStep(
    stepToFind: Step,
    flowVersion: FlowVersion,
): StepWithIndex[] {
    const indexMap = new Map<string, number>()
    const allSteps = getAllSteps(flowVersion)
    const startingPoints = allSteps.filter((step) =>
        flowVersion.connections.every((conn) => conn.targetId !== step.name),
    )

    for (const start of startingPoints) {
        const dfsIndex = { count: 1 }
        const path = _findPathToStep(
            stepToFind,
            start,
            flowVersion,
            allSteps,
            [],
            indexMap,
            new Set(),
            dfsIndex,
        )
        if (path) {
            return path.slice(0, -1)
        }
    }

    throw new Error('Step not found while traversing to find it')
}

function isChildOf(parent: Step, childStepName: string): boolean {
    if (parent.type === ActionType.LOOP_ON_ITEMS && parent.subflow) {
        return parent.subflow.actions.some(
            (action) => action.name === childStepName || isChildOf(action, childStepName),
        )
    }
    if (parent.type === ActionType.ROUTER && parent.children) {
        return parent.children.some(
            (branch) =>
                branch !== null &&
                branch.actions.some(
                    (action) => action.name === childStepName || isChildOf(action, childStepName),
                ),
        )
    }
    return false
}

function findUnusedName(source: string[] | FlowVersion, stepPrefix: string): string {
    let availableNumber = 1
    let availableName = `${stepPrefix}_${availableNumber}`

    const stepNames = Array.isArray(source) ? source : getAllSteps(source).map(step => step.name)
    while (stepNames.includes(availableName)) {
        availableNumber++
        availableName = `${stepPrefix}_${availableNumber}`
    }

    return availableName
}

/**
 * The top-level chain after `start` — children of loops and routers are NOT
 * returned, as the name says: callers use it to find the last first-level step
 * (paste location) or to delete existing actions before an import, where
 * deleting a container already takes its children with it.
 *
 * Only this level's connections are followed. `getStepTargets` aggregates every
 * nesting level, which both descends into containers and exposes the back-edge
 * a loop body has by design (see handleCloseLoopConnections) — that combination
 * recursed until the stack blew up.
 */
function getAllNextActionsWithoutChildren(start: Step, flowVersion: FlowVersion): Action[] {
    const actions: Action[] = []
    const visited = new Set<string>([start.name])

    function traverse(step: Step): void {
        const targets = (flowVersion.connections ?? [])
            .filter((connection) => connection.sourceId === step.name)
            .map((connection) => connection.targetId)
            .filter((targetName): targetName is string => targetName !== undefined)

        if (targets.length === 0) {
            return
        }

        for (const targetName of targets) {
            if (visited.has(targetName)) {
                continue
            }
            const nextStep = flowStructureUtil.getStep(targetName, flowVersion)
            if (!nextStep) {
                continue
            }
            visited.add(targetName)

            if (flowStructureUtil.isAction(nextStep.type)) {
                actions.push(nextStep as Action)
            }

            traverse(nextStep)
        }
    }

    traverse(start)

    return actions
}

function getTriggerFromName(
    flowVersion: FlowVersion,
    triggerName: string,
): Trigger | undefined {
    return flowVersion.triggers.find(
        (trigger: Trigger) => trigger.name === triggerName,
    )
}

function getStepSources(flow: FlowVersion | Subflow, stepName: string): string[] {
    const connections: Connection[] = flow.connections ?? []

    let sources = connections.reduce((acc: string[], conn) => {
        if (conn.targetId === stepName && conn.sourceId !== undefined) {
            acc.push(conn.sourceId)
        }
        return acc
    }, [])

    if (sources.length > 0) {
        return sources
    }

    const actions: Action[] = flow.actions ?? []
    for (const action of actions) {
        if (action.type === ActionType.LOOP_ON_ITEMS && action.subflow) {
            sources = getStepSources(action.subflow, stepName)
            if (sources.length > 0) {
                return sources
            }
        }
    }

    return sources
}
function getStepTargets(
    flow: FlowVersion | Subflow,
    stepName: string,
): string[] {
    const connections: Connection[] = flow.connections ?? []

    let targets = connections.reduce((acc: string[], conn) => {
        if (conn.sourceId === stepName && conn.targetId !== undefined) {
            acc.push(conn.targetId)
        }
        return acc
    }, [])

    const actions: Action[] = flow.actions ?? []
    for (const action of actions) {
        if (action.type === ActionType.LOOP_ON_ITEMS && action.subflow) {
            targets = targets.concat(getStepTargets(action.subflow, stepName))
        }
        else if (action.type === ActionType.ROUTER && action.children) {
            for (const child of action.children) {
                if (child) {
                    targets = targets.concat(getStepTargets(child, stepName))
                }
            }
        }
    }

    return targets
}
function getLoopLastStepName(flowVersion: FlowVersion,
    loopName: string): string | null {
    const loop = getStepOrThrow(loopName, flowVersion)
    if (!loop || loop.type !== ActionType.LOOP_ON_ITEMS || !loop.subflow) {
        return null
    }

    const loopActions = loop.subflow.actions

    let lastElementName: string | null = null
    for (const action of loopActions) {
        const nextActionNames: string[] = getStepTargets(flowVersion, action.name)
        if (nextActionNames.includes(loopName) || nextActionNames.length === 0) {
            lastElementName = action.name
        }
    }

    return lastElementName
}

function getLoopFirstStepName(
    flowVersion: FlowVersion,
    loopName: string,
): string | null {
    const loop = getStepOrThrow( loopName, flowVersion)
    if (!loop || loop.type !== ActionType.LOOP_ON_ITEMS || !loop.subflow) {
        return null
    }
    const loopTargets = getStepTargets(flowVersion, loopName)
    const loopActions = loop.subflow.actions

    const firstLoopAction = loopActions.find((action) =>
        loopTargets.includes(action.name),
    )
    if (firstLoopAction) {
        return firstLoopAction.name
    }

    const lastElementName = getLoopLastStepName(flowVersion, loopName)
    if (!lastElementName) {
        return null
    }

    function findRootSource(
        stepName: string,
        visited: Set<string>,
    ): string | null {
        if (visited.has(stepName)) {
            return null
        }

        visited.add(stepName)

        let sources = getStepSources(flowVersion, stepName)
        const currentAction = getStepOrThrow(stepName, flowVersion)

        if (currentAction?.type === ActionType.LOOP_ON_ITEMS) {
            sources = sources.filter((source) => source !== stepName)
            if (currentAction.subflow?.actions && currentAction.subflow?.actions.length > 0) {
                const loopActionNames = new Set(
                    currentAction.subflow.actions.map((action) => action.name),
                )
                sources = sources.filter(
                    (source) => !loopActionNames.has(source as string),
                )
            }
        }
        if (sources.length === 0) {
            return stepName
        }
        else {
            for (const sourceName of sources) {
                const rootSource = findRootSource(sourceName as string, visited)
                if (rootSource) return rootSource
            }
        }

        return null
    }

    return findRootSource(lastElementName, new Set())
}

function getParentLoopStepName(
    flowVersion: FlowVersion,
    stepName: string,
): string | null {
    // Fonction récursive pour chercher dans les loops et les branches des routeurs
    function findInLoop(actions: Action[]): string | null {
        for (const action of actions) {
            if (action.type === ActionType.LOOP_ON_ITEMS && action.subflow) {
                // Si l'action est directement dans ce loop
                if (stepExistsInSubflow(action.subflow, stepName)) {
                    return action.name
                }
                
                // Chercher récursivement dans les loops de ce subflow
                const nestedResult = findInLoop(action.subflow.actions)
                if (nestedResult) {
                    return nestedResult
                }
            }
            // Chercher aussi dans les branches des routeurs
            else if (action.type === ActionType.ROUTER && action.children) {
                for (const branch of action.children) {
                    if (branch) {
                        const nestedResult = findInLoop(branch.actions)
                        if (nestedResult) {
                            return nestedResult
                        }
                    }
                }
            }
        }
        return null
    }

    return findInLoop(flowVersion.actions)
}

function getParentRouterStepNameAndBranchIndex(
    flowVersion: FlowVersion,
    stepName: string,
): { routerParentName: string, branchIndex: number } | null {
    // Fonction récursive pour chercher dans les branches des routeurs et les subflows des loops
    function findInRouter(actions: Action[]): { routerParentName: string, branchIndex: number } | null {
        for (const action of actions) {
            if (action.type === ActionType.ROUTER && action.children) {
                // Chercher dans les branches du routeur actuel
                for (let index = 0; index < action.children.length; index++) {
                    const branch = action.children[index]
                    if (branch) {
                        // Si l'action est directement dans cette branche
                        if (stepExistsInSubflow(branch, stepName)) {
                            return { routerParentName: action.name, branchIndex: index }
                        }
                        
                        // Chercher récursivement dans les routeurs de cette branche
                        const nestedResult = findInRouter(branch.actions)
                        if (nestedResult) {
                            return nestedResult
                        }
                    }
                }
            }
            // Chercher aussi dans les subflows des loops
            else if (action.type === ActionType.LOOP_ON_ITEMS && action.subflow) {
                const nestedResult = findInRouter(action.subflow.actions)
                if (nestedResult) {
                    return nestedResult
                }
            }
        }
        return null
    }

    return findInRouter(flowVersion.actions)
}

function getExternalLoopTargets(
    flowVersion: FlowVersion,
    loopName: string,
): string[] {
    const loop = getStepOrThrow(loopName, flowVersion)
    if (!loop || loop.type !== ActionType.LOOP_ON_ITEMS) {
        return []
    }

    const loopTargets = getStepTargets(flowVersion, loopName)

    const loopActionNames = new Set(
        loop.subflow?.actions.map((action) => action.name),
    )
    const externalTargets = loopTargets.filter(
        (targetName) => !loopActionNames.has(targetName as string),
    )
    return externalTargets as string[]
}

function stepExistsInSubflow(subflow: Subflow, stepName: string): boolean {
    return subflow.actions.some(step => step.name === stepName)
}

function getBranchLastStepName(branchSubflow: Subflow): string | null {
    for (let i = branchSubflow.actions.length - 1; i >= 0; i--) {
        const action = branchSubflow.actions[i]
        const outgoing = branchSubflow.connections.filter(
            (conn) => conn.sourceId === action.name,
        )
        if (outgoing.length === 0) {
            return action.name
        }
    }
    return null
}

function getBranchFirstStepName(
    flowVersion: FlowVersion,
    routerName: string,
    branchIndex: number,
): string | null {
    const router = flowStructureUtil.getStepOrThrow(routerName, flowVersion) as RouterAction
    if (!router.children || branchIndex >= router.children.length) {
        return null
    }

    const branchSubflow = router.children[branchIndex]
    if (!branchSubflow) {
        return null
    }

    const directConnection = branchSubflow.connections.find(
        (conn) => conn.sourceId === routerName && !!conn.targetId,
    )
    if (directConnection) {
        return directConnection.targetId ?? null
    }

    if (branchSubflow.actions.length === 0) {
        return null
    }

    const lastActionName = getBranchLastStepName(branchSubflow)

    function findRootBranchSource(stepName: string, visited: Set<string>): string | null {
        if (visited.has(stepName)) {
            return null
        }
        visited.add(stepName)

        if (!branchSubflow) {
            return null
        }

        const sources = branchSubflow.connections.reduce((acc: string[], conn) => {
            if (conn.targetId === stepName && conn.sourceId !== undefined) {
                acc.push(conn.sourceId)
            }
            return acc
        }, [])

        if (sources.length === 0) {
            return stepName
        }
        else {
            for (const s of sources) {
                const root = findRootBranchSource(s, visited)
                if (root) {
                    return root
                }
            }
        }
        return null
    }

    return lastActionName ? findRootBranchSource(lastActionName, new Set()) : null
}


function findStepIndex(flowVersion: FlowVersion, stepName: string): number {
    let currentIndex = 1;
    let visited = new Set();

    function traverseFlow(currentStep: Step): number | undefined {
        if (currentStep.name === stepName) {
            return currentIndex;
        }

        if (visited.has(currentStep.name)) {
            return undefined;
        }
        visited.add(currentStep.name);

        currentIndex++;

        const targets = getStepTargets(flowVersion, currentStep.name);
        for (const targetName of targets) {
            const targetStep = getStep(targetName, flowVersion)
            if (targetStep) {
                const foundIndex = traverseFlow(targetStep);
                if (foundIndex !== undefined) {
                    return foundIndex;
                }
            }
        }

        visited.delete(currentStep.name);
        return undefined;
    }

    if (flowVersion.triggers.length > 0) {
        const result = traverseFlow(flowVersion.triggers[0]);
        return result || 0;
    }
    return 0;
}

function findStepByIndex(flowVersion: FlowVersion, index: number): Step | undefined {
    const steps = getAllSteps(flowVersion);
    return steps[index - 1];
}

function getStepParents(flowVersion: FlowVersion, stepName: string): string[] {
    const connections = flowVersion.connections.filter(conn => conn.targetId === stepName)
    return connections.map(conn => conn.sourceId).filter(sourceId => sourceId !== undefined)
}
export const flowStructureUtil = {
    isTrigger, isAction, getAllSteps, getAllStepsInExecutionOrder, transferStep, transferFlow, getStepOrThrow, getActionOrThrow, getTriggerOrThrow, getStep, createBranch, findPathToStep, isChildOf, findUnusedName, getAllNextActionsWithoutChildren, getTriggerFromName, getStepTargets, getLoopLastStepName, getLoopFirstStepName,
    getParentLoopStepName, getExternalLoopTargets, findSubflowForConnection, getParentRouterStepNameAndBranchIndex, getBranchLastStepName, getBranchFirstStepName, findStepIndex, findStepByIndex, getStepParents,
}
